import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './HashCalculator.module.css';

type HashAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';
type SourceType = 'text' | 'file';

const toHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const HashCalculator: React.FC = () => {
  const { t } = useTranslation();
  const [sourceType, setSourceType] = useState<SourceType>('text');
  const [input, setInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [algorithm, setAlgorithm] = useState<HashAlgorithm>('SHA-256');
  const [output, setOutput] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const showMessage = (text: string, error = false) => {
    setMessage(text);
    setIsError(error);
    setTimeout(() => {
      setMessage('');
      setIsError(false);
    }, 2500);
  };

  const handleCalculate = async () => {
    try {
      let hashBuffer: ArrayBuffer;
      if (sourceType === 'text') {
        if (!input) {
          showMessage(t('hashCalculator.messageInputRequired'), true);
          return;
        }
        const data = new TextEncoder().encode(input);
        hashBuffer = await crypto.subtle.digest(algorithm, data);
      } else {
        if (!selectedFile) {
          showMessage(t('hashCalculator.messageSelectFileRequired'), true);
          return;
        }
        const fileBuffer = await selectedFile.arrayBuffer();
        hashBuffer = await crypto.subtle.digest(algorithm, fileBuffer);
      }
      setOutput(toHex(hashBuffer));
    } catch {
      showMessage(t('hashCalculator.messageCalculateFailed'), true);
    }
  };

  const handleCopy = async () => {
    if (!output) {
      showMessage(t('hashCalculator.messageNoResultToCopy'), true);
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      showMessage(t('hashCalculator.messageCopySuccess'));
    } catch {
      showMessage(t('hashCalculator.messageCopyFailed'), true);
    }
  };

  const handleClear = () => {
    setInput('');
    setSelectedFile(null);
    setOutput('');
    setMessage('');
    setIsError(false);
    setAlgorithm('SHA-256');
    setSourceType('text');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('hashCalculator.title')}</h2>
        <p className={styles.description}>{t('hashCalculator.description')}</p>

        {message && <div className={`${styles.tip} ${isError ? styles.error : styles.success}`}>{message}</div>}

        <div className={styles.sourceTabs}>
          <button
            type="button"
            onClick={() => setSourceType('text')}
            className={`${styles.tabButton} ${sourceType === 'text' ? styles.tabButtonActive : ''}`}
          >
            {t('hashCalculator.sourceTabText')}
          </button>
          <button
            type="button"
            onClick={() => setSourceType('file')}
            className={`${styles.tabButton} ${sourceType === 'file' ? styles.tabButtonActive : ''}`}
          >
            {t('hashCalculator.sourceTabFile')}
          </button>
        </div>

        {sourceType === 'text' ? (
          <>
            <div className={styles.label}>{t('hashCalculator.labelInputText')}</div>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className={styles.textarea}
              placeholder={t('hashCalculator.placeholderInputText')}
            />
          </>
        ) : (
          <>
            <div className={styles.label}>{t('hashCalculator.labelChooseFile')}</div>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.fileInput}
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
            <div className={styles.fileMeta}>
              {selectedFile
                ? t('hashCalculator.fileSelected', { name: selectedFile.name, size: selectedFile.size })
                : t('hashCalculator.fileNotSelected')}
            </div>
          </>
        )}

        <div className={styles.controlRow}>
          <label className={styles.selectLabel} htmlFor="hash-algorithm">
            {t('hashCalculator.algorithmLabel')}
          </label>
          <select
            id="hash-algorithm"
            className={styles.select}
            value={algorithm}
            onChange={(e) => setAlgorithm(e.target.value as HashAlgorithm)}
          >
            <option value="SHA-1">SHA-1</option>
            <option value="SHA-256">SHA-256</option>
            <option value="SHA-384">SHA-384</option>
            <option value="SHA-512">SHA-512</option>
          </select>
        </div>

        <div className={styles.actions}>
          <button onClick={handleCalculate} className={styles.primaryButton}>
            {t('hashCalculator.btnCalculate')}
          </button>
          <button onClick={handleCopy} className={styles.secondaryButton}>
            {t('hashCalculator.btnCopy')}
          </button>
          <button onClick={handleClear} className={styles.secondaryButton}>
            {t('hashCalculator.btnClear')}
          </button>
        </div>

        <div className={styles.label}>{t('hashCalculator.resultLabel')}</div>
        <textarea value={output} readOnly className={styles.textarea} placeholder={t('hashCalculator.placeholderResult')} />
      </div>
    </div>
  );
};

export default HashCalculator;
